import { BLUE_400, GRAY_400, GRAY_900 } from 'constants/styling/theme'
import { StarRatingCriterion, StarRatingValue } from 'constants/misc'

import Collapse from '@mui/material/Collapse'
import { FC } from 'react'
import { MUIBadge } from 'components/common/MUIBadge'
import { Nullable } from 'models/helpers'
import Stack from '@mui/material/Stack'
import { useTranslation } from 'react-i18next'

interface Props {
  /** Current rating value for the dimension */
  rating: Nullable<StarRatingValue>
  /** Currently selected criteria */
  selectedCriteria: Set<StarRatingCriterion>
  /** Toggle a single criterion on/off */
  onToggleCriterion: (criterion: StarRatingCriterion) => void
}

const ALL_CRITERIA = Object.values(StarRatingCriterion)

/**
 * Criteria pill chips shown below a star rating dimension.
 * - Rating < 5 -> criteria pill chips (multi-select toggle)
 * - No rating or 5 -> nothing (favourite section shown instead)
 *
 * @example
 * <StarRatingCriteria
 *   rating={3}
 *   selectedCriteria={criteria}
 *   onToggleCriterion={toggle}
 * />
 */
export const StarRatingCriteria: FC<Props> = ({
  rating,
  selectedCriteria,
  onToggleCriterion,
}) => {
  const { t } = useTranslation(['star_rating'])

  return (
    <Collapse in={!!rating && rating < 5}>
      <Stack gap=".8rem" paddingTop=".8rem">
        <Stack direction="row" flexWrap="wrap" gap=".6rem">
          {ALL_CRITERIA.map((criterion) => (
            <MUIBadge
              key={criterion}
              label={t(`star_rating:criteria.${criterion}`)}
              variant={'outlined'}
              color={selectedCriteria.has(criterion) ? 'blue' : 'white'}
              rounded={true}
              onClick={() => onToggleCriterion(criterion)}
              sx={{ cursor: 'pointer', paddingX: '0.8rem' }}
              textColor={GRAY_900}
              borderColor={selectedCriteria.has(criterion) ? BLUE_400 : GRAY_400}
            />
          ))}
        </Stack>
      </Stack>
    </Collapse>
  )
}
